import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const BAR_STYLE = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: '40px',
  zIndex: 3000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '0 12px',
  background: 'var(--warn)',
  color: '#fff',
  fontSize: '14px',
};

const formatRemaining = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// The server reports expires_in_seconds; count down locally from the moment we received it.
function ImpersonationBanner({ impersonating, email }) {
  const { exitViewAs } = useAuth();
  const mountedAt = useRef(Date.now());
  const initial = Number(impersonating?.expires_in_seconds) || 0;
  const [remaining, setRemaining] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const exitedRef = useRef(false);

  const exit = async () => {
    if (busy) return;
    setBusy(true);
    setNotice('');
    try {
      await exitViewAs();   // full reload to /admin on success
    } catch (err) {
      setBusy(false);
      setNotice(err?.response?.data?.error || 'Could not exit the customer view. Try again.');
    }
  };

  useEffect(() => {
    mountedAt.current = Date.now();
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - mountedAt.current) / 1000);
      setRemaining(Math.max(0, initial - elapsed));
    }, 1000);
    return () => clearInterval(timer);
  }, [initial]);

  // At zero the server ends the view on the next request: make that request, which returns us to /admin
  useEffect(() => {
    if (remaining === 0 && !exitedRef.current) {
      exitedRef.current = true;
      exitViewAs().catch(() => {});
    }
  }, [remaining, exitViewAs]);

  // Blocked write attempts (403 Read-only view) are reported by the API client
  useEffect(() => {
    let timer = null;
    const onReadOnly = (event) => {
      setNotice(event.detail || 'Read-only view: this action is disabled while viewing as a customer');
      clearTimeout(timer);
      timer = setTimeout(() => setNotice(''), 6000);
    };
    const onBlocked = (event) => {
      event.preventDefault();   // handled here: no alert fallback
      onReadOnly(event);
    };
    window.addEventListener('view-as-readonly', onReadOnly);
    window.addEventListener('app-request-blocked', onBlocked);
    return () => {
      window.removeEventListener('view-as-readonly', onReadOnly);
      window.removeEventListener('app-request-blocked', onBlocked);
      clearTimeout(timer);
    };
  }, []);

  return (
    <div style={BAR_STYLE} role="status">
      <span>
        Viewing as <strong>{email}</strong> - read-only. Ends in {formatRemaining(remaining)}
      </span>
      {notice && (
        <span style={{ background: 'var(--warn-soft)', color: 'var(--warn)', padding: '2px 8px', borderRadius: '4px' }}>
          {notice}
        </span>
      )}
      <button
        type="button"
        onClick={exit}
        disabled={busy}
        style={{ background: '#fff', color: 'var(--warn)', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer', fontWeight: 600 }}
      >
        {busy ? 'Exiting...' : 'Exit'}
      </button>
    </div>
  );
}

export default ImpersonationBanner;
